import { Button, Col, Row } from 'design-react-kit/dist/design-react-kit';
import { defineMessages, useIntl } from 'react-intl';
import { useEffect, useState } from 'react';

import EmailField from './EmailField';
import EndDateField from './EndDateField';
import ListField from './ListField';
import NumberField from './NumberField';
import SourceField from './SourceField';
import StartDateField from './StartDateField';
import TextField from './TextField';
import { Toast } from '@plone/volto/components';
import { toast } from 'react-toastify';

const messages = defineMessages({
  sendButton: {
    id: 'Invia richiesta',
    defaultMessage: 'Invia richiesta',
  },
  and: {
    id: 'e',
    defaultMessage: 'e',
  },
  correctErrors: {
    id: '* Correggere gli errori sopra indicati',
    defaultMessage: '* Correggere gli errori sopra indicati',
  },
  requiredField: {
    id: '* Campo obbligatorio',
    defaultMessage: '* Campo obbligatorio',
  },
  success: {
    id: 'Request successfully created',
    defaultMessage: 'Richiesta creata con successo',
  },
});

const HistoricalData = ({ data }) => {
  const intl = useIntl();
  const apiBaseUrl = `https://api.arpa.veneto.it/extraction_request/add`;
  const [StartDate, setStartDate] = useState(null);
  const [EndDate, setEndDate] = useState(null);
  const [formStartDate, setFormStartDate] = useState(null);
  const [formEndDate, setFormEndDate] = useState(null);
  const [startDateError, setStartDateError] = useState();
  const [endDateError, setEndDateError] = useState();
  const [emailError, setEmailError] = useState();
  const [formData, setFormData] = useState({});
  const [fieldsErrors, setFieldsErrors] = useState({});

  let format = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  };

  useEffect(() => {
    if (
      formData?.startDate &&
      (formData?.startDate > StartDate || formData?.startDate < StartDate)
    ) {
      setFormStartDate(new Date(formData.startDate).toLocaleDateString('it-IT', format));
      setStartDate(formData.startDate);
    }
    if (formData?.endDate && (formData?.endDate > EndDate || formData?.endDate < EndDate)) {
      setFormEndDate(new Date(formData.endDate).toLocaleDateString('it-IT', format));
      setEndDate(formData.endDate);
    }
  }, [formData]);

  const createRequestData = () => {
    let requestData = {
      scheduled_endpoint: data?.datasetCode,
      email: formData?.email,
      parameters: {},
    };

    /* Add date parameters */
    if (data?.fromDateProp && formStartDate) {
      requestData.parameters[data.fromDateProp] = formStartDate;
    }
    if (data?.toDateProp && formEndDate) {
      requestData.parameters[data.toDateProp] = formEndDate;
    }

    /* Add filter parameters */
    data?.filters?.forEach(filter => {
      const propertyField = filter.propertyField || filter.labelField;
      requestData.parameters[propertyField] = formData[propertyField];
    });

    return requestData;
  };

  const sendRequest = async () => {
    try {
      /* Construct API URL with parameters */
      const requestData = createRequestData();
      const response = await fetch(apiBaseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });
      const responseData = await response.json();

      response?.ok
        ? (toast.success(
          <Toast
            success
            title={intl.formatMessage({ id: 'Success' })}
            content={intl.formatMessage(messages.success)}
          />
        ),
          setFormData({}))
        : toast.error(
          <Toast
            error
            title={intl.formatMessage({ id: 'Error' })}
            content={responseData?.msg ? responseData?.msg : response?.statusText}
          />
        );
    } catch (error) {
      toast.error(
        <Toast error title={intl.formatMessage({ id: 'Error' })} content={error.toString()} />
      );
    }
  };

  const handleSubmit = e => {
    e.preventDefault();
    let hasErrors = false;

    /* email check */
    if (!formData?.email) {
      setEmailError(intl.formatMessage(messages.requiredField));
      hasErrors = true;
    }

    /* dates check */
    if (
      !formData?.startDate ||
      !(formData.startDate instanceof Date) ||
      isNaN(formData.startDate.getTime())
    ) {
      setStartDateError(intl.formatMessage(messages.requiredField));
      hasErrors = true;
    }
    if (
      !formData?.endDate ||
      !(formData.endDate instanceof Date) ||
      isNaN(formData.endDate.getTime())
    ) {
      setEndDateError(intl.formatMessage(messages.requiredField));
      hasErrors = true;
    }

    /* Validate all mandatory fields */
    data?.filters?.forEach(filter => {
      const fieldName = filter?.propertyField || filter?.labelField;
      if (filter?.mandatory) {
        const value = formData[fieldName];
        if (!value) {
          hasErrors = true;
          fieldsErrors[fieldName] = intl.formatMessage(messages.requiredField);
        }
      }
    });

    /* Prevent submission if there are errors */
    if (!hasErrors && !startDateError && !endDateError) {
      sendRequest();
    }
  };

  return (
    <form className="request-form mt-4">
      <Row className={'mt-5 mb-5'}>
        <StartDateField
          data={data}
          formData={formData}
          setFormData={setFormData}
          endDate={EndDate}
          error={startDateError}
          setError={setStartDateError}
        />
        <EndDateField
          data={data}
          formData={formData}
          setFormData={setFormData}
          startDate={StartDate}
          error={endDateError}
          setError={setEndDateError}
        />
      </Row>
      <Row className={'mt-2 mb-2'}>
        {Array.isArray(data?.filters) &&
          data?.filters?.map((filter, index) => (
            <Col key={index} md={6} className="mt-5 mb-5">
              {filter?.filterType === 'selectList' && (
                <ListField
                  formData={formData}
                  setFormData={setFormData}
                  filter={filter}
                  error={fieldsErrors[filter?.propertyField]}
                  setError={setFieldsErrors}
                />
              )}
              {filter?.filterType === 'source' && (
                <SourceField
                  formData={formData}
                  setFormData={setFormData}
                  filter={filter}
                  error={fieldsErrors[filter?.propertyField]}
                  setError={setFieldsErrors}
                />
              )}
              {filter?.filterType === 'text' && (
                <TextField
                  formData={formData}
                  setFormData={setFormData}
                  filter={filter}
                  error={fieldsErrors[filter?.propertyField]}
                  setError={setFieldsErrors}
                />
              )}
              {filter?.filterType === 'number' && (
                <NumberField
                  formData={formData}
                  setFormData={setFormData}
                  filter={filter}
                  error={fieldsErrors[filter?.propertyField]}
                  setError={setFieldsErrors}
                />
              )}
            </Col>
          ))}
      </Row>
      <Row className={'mt-4 mb-2'}>
        <EmailField
          formData={formData}
          setFormData={setFormData}
          error={emailError}
          setError={setEmailError}
        />
      </Row>
      <Row className={'mt-4'}>
        <Button name="buttonSubmit" type="submit" color="primary" onClick={handleSubmit}>
          {intl.formatMessage(messages.sendButton)}
        </Button>
      </Row>
    </form>
  );
};

export default HistoricalData;
